import { useLocation } from 'react-router-dom';
import { Phone } from 'lucide-react';
import { WHATSAPP_HREF, WhatsAppIcon } from '@/components/WhatsAppButton';
import { trackGa4Event } from '@/lib/ga4';
import { trackEvent } from '@/lib/metaPixel';

// Always-visible call + WhatsApp strip that sits at the very top of the fixed header,
// so it stays on screen while visitors scroll (phones included).
// Hidden on staff pages and on the paid-ads landing page, which has its own contact bar.
const PHONE_DISPLAY = '0208 935 5492';
const PHONE_HREF = 'tel:+442089355492';
const HIDDEN_PATHS = ['/admin', '/inventory-offer'];

// Height of the strip — Layout adds a spacer of the same height so no page content sits under it.
export const CONTACT_BAR_HEIGHT_CLASS = 'h-11';

export function useContactBarVisible() {
  const { pathname } = useLocation();
  return !HIDDEN_PATHS.some(p => pathname.startsWith(p));
}

function trackContact(method) {
  // Lets GA4 and Meta count calls and WhatsApp chats as leads from each page.
  // Wrapped so a blocked/broken tracker can never stop the call or chat opening.
  try {
    trackGa4Event('contact_click', { method, page: window.location.pathname });
    trackEvent('Contact', { content_name: method });
  } catch { /* never block the tap */ }
}

// Phones: open WhatsApp in the same tab so the phone hands straight over to the WhatsApp app.
// A new tab (_blank) is often blocked or opens blank inside Facebook/Instagram's in-app browser.
// Computers: keep a new tab so visitors don't lose the website.
const isTouch = () => {
  try { return window.matchMedia('(pointer: coarse)').matches; } catch { return false; }
};

const TAP_STYLE = { touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent' };

export default function TopContactBar() {
  return (
    <div className="relative z-10 bg-navy text-white border-b border-white/10">
      <div className={`max-w-[1600px] mx-auto md:px-10 lg:px-16 flex items-stretch ${CONTACT_BAR_HEIGHT_CLASS}`}>
        <span className="hidden md:flex items-center caps-label text-white/70">
          Open 7 days&nbsp;&nbsp;·&nbsp;&nbsp;9am–6pm&nbsp;&nbsp;·&nbsp;&nbsp;London &amp; Manchester
        </span>

        <div className="flex flex-1 md:flex-none md:ml-auto items-stretch">
          <a
            href={PHONE_HREF}
            onClick={() => trackContact('phone')}
            style={TAP_STYLE}
            aria-label={`Call Daley Inventories on ${PHONE_DISPLAY}`}
            className="flex-1 md:flex-none flex items-center justify-center gap-2 px-3 md:px-4 bg-orange md:bg-transparent text-navy md:text-orange text-sm md:text-base font-bold tracking-tight whitespace-nowrap hover:bg-orange-dark md:hover:bg-transparent md:hover:text-orange-light transition-colors"
          >
            <Phone size={16} strokeWidth={2.5} />
            <span><span className="hidden min-[360px]:inline md:hidden">Call </span>{PHONE_DISPLAY}</span>
          </a>

          <span className="hidden md:block w-px my-3 bg-white/20" aria-hidden="true" />

          <a
            href={WHATSAPP_HREF}
            target={isTouch() ? undefined : '_blank'}
            rel="noopener noreferrer"
            onClick={() => trackContact('whatsapp')}
            style={TAP_STYLE}
            aria-label="Chat with Daley Inventories on WhatsApp"
            className="flex-1 md:flex-none flex items-center justify-center gap-2 px-3 md:pl-4 md:pr-0 text-white text-sm md:text-base font-bold tracking-tight whitespace-nowrap hover:text-[#25D366] transition-colors"
          >
            <WhatsAppIcon className="w-5 h-5 text-[#25D366]" />
            WhatsApp us
          </a>
        </div>
      </div>
    </div>
  );
}
